import { Controller } from "@hotwired/stimulus";

// プロジェクト一覧の Turbo Frame を再取得して、運用状況・振込状況などの最新状態を反映する。
// - 最新に更新ボタン (click->list-refresh#refresh) で手動更新
// - 自動更新はチェックボックス (change->list-refresh#toggleAutoUpdateMode) で ON/OFF。初期値 OFF
//   - ON の間 intervalValue (ms) ごとに更新し、maxDurationValue (ms) 経過で自動停止する
//   - 非表示タブ中 (document.hidden) はスキップする
//
// 状態は autoValue (Boolean) の1つだけ。
// ON/OFF の切り替えは必ず autoValue への代入で行い、副作用は autoValueChanged に集約する。
//
// 更新用 URL はフレーム内の data-list-refresh-target="source" から取得する。
// フレームの子要素はソート・更新のたびにサーバーが最新の絞り込み・ソート条件で描画し直すため、source が常に唯一の最新条件を表す。
class ListRefreshController extends Controller {
  static targets = ["autoUpdateCheckbox", "source"];
  static values = {
    auto: { type: Boolean, default: false },
    interval: { type: Number, default: 15000 }, // 15秒
    maxDuration: { type: Number, default: 3600000 }, // 1時間
  };

  // 自動更新タイマーの実体と停止期限。
  // null は「停止中」を表し、設定・解除は startTimer / stopTimer だけが対で行う。
  // JS 実行中にしか意味を持たない内部ハンドル・導出値のため、DOM 属性と同期される static values には置かない
  timer = null;
  autoDeadline = null;

  // --- Stimulus が自動で呼ぶコールバック（発火順） ---

  // Stimulus Values API の change callback（autoValue が変わるたびに Stimulus が自動で呼ぶ。接続時の初期値設定でも1回呼ばれる）。
  // 自動更新の開始・停止に伴う副作用（タイマー・チェックボックス表示）はすべてここで行う
  autoValueChanged(enabled) {
    if (this.hasAutoUpdateCheckboxTarget) this.autoUpdateCheckboxTarget.checked = enabled;

    if (enabled) {
      this.startTimer();
    } else {
      this.stopTimer();
    }
  }

  // Stimulus の target connected callback。
  // 対象要素（data-list-refresh-target="autoUpdateCheckbox"）が DOM に現れるたびに Stimulus が自動で呼ぶ。
  // 発火タイミングは次の2つ。
  //   1. 初期表示でコントローラが接続された直後（既存の自動更新チェックボックスに対して1回）
  //   2. 最新に更新・自動更新・ソートでフレームの中身が丸ごと差し替わるとき。
  //      チェックボックスの input 要素も（見た目は同じでも別物の）新しい要素として作り直され、その挿入直後に発火する
  // チェックボックスはサーバー側で常に未チェックで描画されるため、2 のたびに見た目だけ OFF に戻ってしまう。
  // ON/OFF の実状態は（差し替わらない）フレーム要素側の autoValue に生きているので、ここで表示を実状態に合わせ直す
  autoUpdateCheckboxTargetConnected(element) {
    element.checked = this.autoValue;
  }

  disconnect() {
    this.stopTimer();
  }

  // --- HTML (data-action) から呼ばれるアクション ---

  // 自動更新チェックボックス（view の data-action="change->list-refresh#toggleAutoUpdateMode"）から呼ばれるアクション
  toggleAutoUpdateMode(event) {
    this.autoValue = event.target.checked;
  }

  // 最新に更新ボタン（view の data-action="click->list-refresh#refresh"）と tick から呼ばれる
  refresh() {
    const url = new URL(this.sourceTarget.dataset.url, window.location.origin);
    // チェック済みの selected_project_ids[] を付与して選択状態を維持する。
    // (form-checkbox-sum#preserveSelection と同じ仕組み。Rails の配列パラメータは
    //  同名キーの繰り返し形式のため、URLSearchParams#append で1件ずつ追加する)
    this.element
      .querySelectorAll('input[data-form-checkbox-sum-target="checkbox"]:checked')
      .forEach((cb) => url.searchParams.append("selected_project_ids[]", cb.value));

    if (this.element.getAttribute("src") === url.toString()) {
      // src が変わらない（選択状態も同じ）場合、src への再代入では再取得されないため reload する
      this.element.reload();
    } else {
      this.element.src = url.toString();
    }
  }

  // --- 内部ヘルパー ---

  tick() {
    if (Date.now() >= this.autoDeadline) {
      this.autoValue = false;
      return;
    }
    // 非表示タブ（別タブ表示中・最小化中）では更新しても誰も見ないため、サーバーへの無駄なリクエストを省く。
    // タイマーは止めないので、タブに戻れば次の tick から更新が再開される
    if (document.hidden) return;

    this.refresh();
  }

  startTimer() {
    this.stopTimer();

    // つけっぱなしで放置してもリクエストが飛び続けないよう、停止期限を持つ
    this.autoDeadline = Date.now() + this.maxDurationValue;
    this.timer = setInterval(() => this.tick(), this.intervalValue);
  }

  stopTimer() {
    if (!this.timer) return;

    clearInterval(this.timer);
    this.timer = null;
    this.autoDeadline = null;
  }
}

// Registered in admin.js
export { ListRefreshController };
